<!--
  Левая палитра конструктора паспорта: поиск и группы полей из новой fields-модели.
-->
<script setup lang="ts">
import { computed } from 'vue'
import type {
  DragPayload,
  PaletteField,
  PaletteGroupKind,
} from '../model/types'
import { useDragDrop } from '../model/useDragDrop'
import { usePalette } from '../model/usePalette'
import PassportBuilderSidebarGroup from './PassportBuilderSidebarGroup.vue'

export type PassportBuilderPaletteApi = ReturnType<typeof usePalette>
export type PassportBuilderDragDropApi = ReturnType<typeof useDragDrop>

const props = defineProps<{
  palette: PassportBuilderPaletteApi
  dragDrop: PassportBuilderDragDropApi
}>()

const emit = defineEmits<{
  dragstart: [payload: DragPayload, event: DragEvent]
  'open-own-fields': []
  'open-reference-fields': []
  'open-links': []
  /** Sidebar chip click: jump to an already-placed canvas field (any tab). */
  'palette-field-activate': [field: PaletteField]
}>()

const paletteGroups = computed(() => props.palette.groups.value)
const dragActive = computed(() => props.dragDrop.dragData.value != null)

const searchModel = computed({
  get: () => props.palette.searchQuery.value,
  set: (value: string) => {
    props.palette.searchQuery.value = value
  },
})

function onChipDragStart(payload: DragPayload, event: DragEvent) {
  props.dragDrop.startDrag(payload, event)
  emit('dragstart', payload, event)
}

function onChipDragEnd() {
  props.dragDrop.endDrag()
}

function openSettingsByKind(kind: PaletteGroupKind) {
  if (kind === 'own') {
    emit('open-own-fields')
    return
  }

  if (kind === 'parent') {
    emit('open-reference-fields')
    return
  }

  if (kind === 'element') {
    emit('open-links')
  }
}
</script>

<template>
  <aside class="pb-sidebar">
    <div class="pb-sidebar-search">
      <VTextField
        v-model="searchModel"
        variant="outlined"
        density="compact"
        hide-details="auto"
        placeholder="Поиск полей..."
        class="pb-sidebar-search-field"
      />
    </div>

    <div class="pb-sidebar-tree" :class="{ 'pb-drag-active': dragActive }" @dragend="onChipDragEnd">
      <div class="pb-sidebar-tree__palette-wrap">
        <div
          v-if="palette.isLoading.value"
          class="pb-sidebar-field-loader"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <VProgressCircular
            indeterminate
            :size="48"
            :width="4"
            color="primary"
          />
        </div>

        <div class="pb-sidebar-tree__groups">
          <PassportBuilderSidebarGroup
            v-for="group in paletteGroups"
            :key="group.id"
            :group="group"
            show-edit
            @toggle="palette.toggleGroup"
            @dragstart="onChipDragStart"
            @field-activate="emit('palette-field-activate', $event)"
            @edit="openSettingsByKind(group.kind)"
          />
        </div>
      </div>
    </div>
  </aside>
</template>

<style scoped lang="scss">
.pb-sidebar-search-field {
  font-size: 13px;
}
</style>
